import { SetStateAction, Dispatch, useEffect } from 'react';
import { FormControl, Stack, Typography, useMediaQuery } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { VigoTextInput } from 'src/@vigo/vigo-textinput';
import LoadingButton from '@mui/lab/LoadingButton';
import VigoDatePicker from 'src/@vigo/vigo-datepicker';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { QueryData } from '..';
import { VigoTypography } from 'src/@vigo/vigo-typography';

interface SearchFormProps {
  submitted: boolean;
  T_DURATION: string;
  T_TYPE: string;
  setQueryData: Dispatch<SetStateAction<QueryData>>;
  isLoading: boolean;
  orderFetchError: boolean;
  multipleRecordsExist: boolean;
  noDataFound: boolean;
  refetch: any;
  queryParamValues: QueryData | null;
}

const schema = (delDateCondition: boolean) =>
  yup.object().shape(
    {
      postcode: yup.string().required('A postcode is required '),
      trackingCode: yup.string().when(['consignmentNumber', 'orderRef'], {
        is: (consignmentNumber: any, orderRef: any) => !consignmentNumber && !orderRef,
        then: yup.string().required(''),
        otherwise: yup.string().optional()
      }),
      consignmentNumber: yup.string().when(['trackingCode', 'orderRef'], {
        is: (trackingCode: any, orderRef: any) => !trackingCode && !orderRef,
        then: yup.string().required(''),
        otherwise: yup.string().optional()
      }),
      orderRef: yup.string().when(['consignmentNumber', 'trackingCode'], {
        is: (consignmentNumber: any, trackingCode: any) => !consignmentNumber && !trackingCode,
        then: yup.string().required(''),
        otherwise: yup.string().optional()
      }),
      deliveryDate: yup.string().when([], {
        is: () => delDateCondition,
        then: yup.string().required('Delivery date is required'),
        otherwise: yup.string().optional()
      })
    },
    [
      ['consignmentNumber', 'trackingCode'],
      ['trackingCode', 'orderRef'],
      ['consignmentNumber', 'orderRef']
    ]
  );

const SearchForm = ({
  submitted,
  T_DURATION,
  T_TYPE,
  setQueryData,
  isLoading,
  orderFetchError,
  multipleRecordsExist,
  noDataFound,
  refetch,
  queryParamValues
}: SearchFormProps) => {
  const isColumnLayout = useMediaQuery('(max-width: 1000px)');

  const { control, handleSubmit, formState, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      postcode: '',
      trackingCode: '',
      consignmentNumber: '',
      orderRef: '',
      deliveryDate: ''
    },
    resolver: yupResolver(schema(multipleRecordsExist && noDataFound))
  });

  const onSubmit = (data: any) => {
    setQueryData(data);

    refetch();
  };

  const onError = () => {
    console.log('error');
  };

  const displayAdditionalFieldsError = () => {
    const { errors = {} } = formState;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { postcode = '', ...rest } = errors;

    return Object.keys(rest).length > 0;
  };

  useEffect(() => {
    if (!queryParamValues) return;

    Object.entries(queryParamValues ?? {}).forEach(([key, value]) => {
      if (value) {
        setValue(key as keyof QueryData, value);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParamValues]);

  return (
    <Stack
      direction="column"
      gap="30px"
      sx={{
        backgroundColor: '#fff',
        padding: '18px 40px',
        borderRadius: '10px',
        position: 'absolute',
        top: '27%',
        right: submitted ? '50%' : '9.5%',
        ...(submitted && { transform: 'translateX(61%)' }),
        width: 'min(400px, 90%)',
        ...(!isColumnLayout && { transition: `all ${T_DURATION} ${T_TYPE}}` }),
        '@media (max-width: 1300px)': {
          ...(submitted && { top: '24%', right: '50%', transform: 'translateX(50%)' })
        },
        '@media (max-width: 1000px)': {
          ...(submitted && { top: '29%' })
        },
        '@media (max-width: 920px)': {
          ...(!submitted && { top: '24%', left: '50%', transform: 'translateX(-50%)' })
        },
        '@media (max-width: 660px)': {
          top: '26%'
        },
        '@media (max-width: 636px)': {
          top: '31%'
        }
      }}>
      <Typography
        sx={{
          fontSize: '26px',
          fontWeight: 'medium',
          letterSpacing: '0px',
          color: '#2B2D42'
        }}>
        Track my Order
      </Typography>

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Stack direction="column" gap="20px">
          <FormControl fullWidth>
            <Controller
              control={control}
              name="postcode"
              render={({ field, fieldState: { error } }) => (
                <VigoTextInput
                  {...field}
                  fullWidth
                  required={true}
                  label="Delivery Postcode"
                  InputProps={{ sx: { height: '40px' } }}
                  inputLabelProps={{ sx: { fontSize: '1rem' } }}
                  value={field.value || ''}
                  error={!!error}
                  errormessage={error?.message}
                />
              )}
            />
          </FormControl>

          <Typography sx={{ fontSize: '1rem', fontWeight: 'medium', color: '#2b2d42', letterSpacing: '0px' }}>
            Plus, one of the following
          </Typography>

          {!multipleRecordsExist && (
            <FormControl fullWidth>
              <Controller
                control={control}
                name="trackingCode"
                render={({ field, fieldState: { error } }) => (
                  <VigoTextInput
                    {...field}
                    fullWidth
                    label="Tracking Code"
                    InputProps={{
                      sx: {
                        height: '40px'
                      }
                    }}
                    inputLabelProps={{ sx: { fontSize: '1rem' } }}
                    value={field.value || ''}
                    error={!!error}
                    errormessage={error?.message}
                  />
                )}
              />
            </FormControl>
          )}

          <FormControl fullWidth>
            <Controller
              control={control}
              name="consignmentNumber"
              render={({ field, fieldState: { error } }) => (
                <VigoTextInput
                  {...field}
                  fullWidth
                  label="Consignment Number"
                  InputProps={{ sx: { height: '40px' } }}
                  inputLabelProps={{ sx: { fontSize: '1rem' } }}
                  value={field.value || ''}
                  error={!!error}
                  errormessage={error?.message}
                />
              )}
            />
          </FormControl>

          {!multipleRecordsExist && (
            <FormControl fullWidth>
              <Controller
                control={control}
                name="orderRef"
                render={({ field, fieldState: { error } }) => (
                  <VigoTextInput
                    {...field}
                    fullWidth
                    label="Order Ref"
                    InputProps={{ sx: { height: '40px' } }}
                    inputLabelProps={{ sx: { fontSize: '1rem' } }}
                    value={field.value || ''}
                    error={!!error}
                    errormessage={error?.message}
                  />
                )}
              />
            </FormControl>
          )}

          {noDataFound && (
            <VigoTypography
              sx={{ color: 'red', textAlign: 'center' }}
              value="We could not find any tracking information with the data provided, please check and try again."
            />
          )}

          {multipleRecordsExist && (
            <Typography sx={{ color: 'red', textAlign: 'center' }}>
              We have a number of records with this ref. Please enter a delivery date.
            </Typography>
          )}

          {multipleRecordsExist && (
            <FormControl fullWidth>
              <Controller
                control={control}
                name="deliveryDate"
                render={({ field, fieldState: { error } }) => (
                  <VigoDatePicker
                    name={field.name}
                    label="Delivery Date"
                    value={new Date(field.value) || undefined}
                    onChange={field.onChange}
                    error={!!error}
                    errormessage={error?.message}
                  />
                )}
              />
            </FormControl>
          )}

          {displayAdditionalFieldsError() && (
            <Typography sx={{ color: 'red', textAlign: 'center' }}>
              &#8226; At least one additional field is required
            </Typography>
          )}

          {orderFetchError && (
            <Typography sx={{ color: 'red', textAlign: 'center' }}>
              Sorry, we don't recognise that number. Please check the information to make sure your details are correct
            </Typography>
          )}

          <LoadingButton
            loading={isLoading}
            type="submit"
            sx={{
              width: '100%',
              backgroundColor: '#2b2d42',
              color: '#fff',
              font: 'normal normal bold 16px/20px Quicksand',
              letterSpacing: '0px',
              borderRadius: '4px',
              height: '56px',
              textTransform: 'capitalize',
              marginTop: '5px',
              '&:hover': {
                backgroundColor: '#626679'
              },
              '& .MuiLoadingButton-loadingIndicator': {
                color: '#fff !important'
              }
            }}>
            Find
          </LoadingButton>
        </Stack>
      </form>
    </Stack>
  );
};

export default SearchForm;
